/**
 * useOfflineQueue — bridges the page to the service-worker outbox.
 *
 * Public API:
 *   const { online, enqueue, drained } = useOfflineQueue();
 *
 *   • online:   navigator.onLine state (kept in sync via window events)
 *   • enqueue:  async (url, init) => "queued" | "online" | "unsupported"
 *               When offline, posts a structured message to the SW which
 *               persists the request in IndexedDB and tries to register
 *               a Background Sync. When online, returns "online" so the
 *               caller can fall through to a normal `fetch`.
 *   • drained:  count of mutations the SW has flushed since page load —
 *               components can subscribe to bump a "X requests sent"
 *               toast.
 *
 * Why a hook + SW message-channel and not a fetch interceptor?
 *   • Service workers can't intercept POST bodies that are FormData
 *     streams (we'd lose the body on first network failure).
 *   • Putting the enqueue decision in the page lets us show a
 *     friendly "Saved offline — will send when reconnected" toast
 *     instead of a generic network error.
 *   • Keeps the SW logic small and testable.
 */
import { useCallback, useEffect, useState } from "react";

export default function useOfflineQueue() {
  const [online, setOnline] = useState(
    typeof navigator !== "undefined" ? navigator.onLine !== false : true
  );
  const [drained, setDrained] = useState(0);
  const [failed, setFailed] = useState(0);

  useEffect(() => {
    const onUp = () => {
      setOnline(true);
      // Hint the SW to flush as soon as we come back. Background Sync
      // will also fire, but the explicit hint covers browsers that
      // don't support it (Safari, Firefox).
      navigator.serviceWorker?.controller?.postMessage({ type: "FLUSH_OUTBOX" });
    };
    const onDown = () => setOnline(false);
    window.addEventListener("online", onUp);
    window.addEventListener("offline", onDown);
    // Listen to SW broadcasts so any open tab can update its UI.
    const onMsg = (e) => {
      if (e.data?.type === "OUTBOX_DRAINED") setDrained((n) => n + 1);
      if (e.data?.type === "OUTBOX_FAILED")  setFailed((n) => n + 1);
    };
    if (navigator.serviceWorker) {
      navigator.serviceWorker.addEventListener("message", onMsg);
    }
    return () => {
      window.removeEventListener("online", onUp);
      window.removeEventListener("offline", onDown);
      if (navigator.serviceWorker) {
        navigator.serviceWorker.removeEventListener("message", onMsg);
      }
    };
  }, []);

  /**
   * enqueue(url, init) — call this BEFORE attempting a network request
   * if you want the offline outbox semantics. When offline, returns
   * "queued"; the caller should NOT call fetch and should show the
   * "saved for later" toast. When online, returns "online" so the
   * caller falls through to a normal fetch.
   */
  const enqueue = useCallback(async (url, init = {}) => {
    if (online) return "online";
    if (!navigator.serviceWorker?.controller) return "unsupported";
    // Serialize headers — Headers / Map need to become a plain object
    // for structured-clone over the SW message channel.
    const hdrs = {};
    if (init.headers) {
      if (init.headers.forEach) {
        init.headers.forEach((v, k) => { hdrs[k] = v; });
      } else {
        Object.assign(hdrs, init.headers);
      }
    }
    // Body must be string-serializable. Caller passes JSON; for
    // FormData / Blob we degrade to "unsupported" since IndexedDB
    // can't structure-clone a stream reliably across SW restarts.
    let body = init.body;
    if (body && typeof body !== "string") {
      try { body = JSON.stringify(body); } catch (_) { return "unsupported"; }
    }
    const channel = new MessageChannel();
    return new Promise((resolve) => {
      const t = setTimeout(() => resolve("queued"), 1500);  // hard fallback
      channel.port1.onmessage = (e) => {
        clearTimeout(t);
        resolve(e.data?.ok ? "queued" : "unsupported");
      };
      navigator.serviceWorker.controller.postMessage({
        type: "OUTBOX_ENQUEUE",
        url, method: init.method || "POST", headers: hdrs, body,
      }, [channel.port2]);
    });
  }, [online]);

  return { online, enqueue, drained, failed };
}
